/**
 * Utility functions for generating case titles from plaintiffs and defendants
 * Based on backend TitleExtensions logic
 */

const MAX_DEFENDANTS_IN_TITLE = 3;

/**
 * Remove HTML tags from text
 */
const cleanupHtmlTags = (text: string): string => {
  return text.replace(/<[^>]*>/g, '').trim();
};

/**
 * Compress array of names into a single string
 */
const compress = (items: string[], separator: string = '; '): string => {
  if (!items || items.length === 0) {
    return '';
  }
  return items
    .map(x => cleanupHtmlTags(x))
    .filter(x => x.length > 0)
    .join(separator);
};

/**
 * Get first item from semicolon-separated string
 */
const getFirst = (text: string): string => {
  if (!text || text.trim().length === 0) {
    return '';
  }
  const parts = text.split(';').filter(x => x.trim().length > 0);
  return parts.length > 0 ? parts[0].trim() : '';
};

/**
 * Generate short title for cases
 * Example 1: {Plaintiff} v. {Defendant 1}, {Defendant 2}
 * Example 2: {Plaintiff} v. {Defendant 1}, {Defendant 2}, {Defendant 3}, et al.
 */
export const getCaseTitle = (
  plaintiffs: string[] | undefined | null,
  defendants: string[] | undefined | null
): string => {
  const plaintiffsList = plaintiffs || [];
  const defendantsList = defendants || [];

  const firstPlaintiff = getFirst(compress(plaintiffsList));
  const limitedDefendants = defendantsList.slice(0, MAX_DEFENDANTS_IN_TITLE);
  const defendantsString = compress(limitedDefendants, ', ');

  // If we have both plaintiff and defendant, create title
  if (firstPlaintiff && defendantsString) {
    return getTitleNameShort(firstPlaintiff, defendantsString, defendantsList.length);
  }

  // Otherwise, use the fallback logic
  return getNameForOthers(plaintiffsList, defendantsList);
};

/**
 * Generate title for reports (full names)
 */
export const getCaseTitleForReports = (
  plaintiffs: string[] | undefined | null,
  defendants: string[] | undefined | null
): string => {
  const plaintiffsList = plaintiffs || [];
  const defendantsList = defendants || [];

  const firstDefendant = getFirst(compress(defendantsList));
  const firstPlaintiff = getFirst(compress(plaintiffsList));
  const hasBoth = firstDefendant.length > 0 && firstPlaintiff.length > 0;

  if (hasBoth) {
    return getTitleName(plaintiffsList, defendantsList);
  } else {
    return getNameForOthers(plaintiffsList, defendantsList);
  }
};

/**
 * Get name when only one party is available
 */
const getNameForOthers = (plaintiffs: string[], defendants: string[]): string => {
  const plaintiffsName = getPartName(compress(plaintiffs));
  if (plaintiffsName) {
    return plaintiffsName;
  }

  const defendantsName = getPartName(compress(defendants));
  if (defendantsName) {
    return defendantsName;
  }

  return '';
};

/**
 * Generate full title name
 */
const getTitleName = (plaintiffs: string[], defendants: string[]): string => {
  return `${compress(plaintiffs)} v. ${compress(defendants)}`;
};

/**
 * Generate short title name with et al. if needed
 */
const getTitleNameShort = (
  plaintiff: string,
  defendants: string,
  defendantsCount: number
): string => {
  const etAl = defendantsCount > MAX_DEFENDANTS_IN_TITLE ? ', et al.' : '';
  return `${plaintiff} v. ${defendants}${etAl}`;
};

/**
 * Get part name with "In re:" prefix if single party
 */
const getPartName = (originalName: string): string | null => {
  if (!originalName || originalName.length === 0) {
    return null;
  }

  if (!originalName.includes(';')) {
    return `In re: ${originalName}`;
  } else {
    return originalName;
  }
};
